import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Button, Card, EmptyState, Icon, Skeleton, Tag, useConfirm, useToast } from "@xinghai/ui";
import { accountApi } from "../../lib/api";
import type { DeviceSession } from "../../lib/api";
import { friendlyMessage } from "../../lib/apiErrors";
import { qk } from "../../lib/queries";
import { deviceLabel } from "../../lib/ua";

function when(iso: string | null | undefined): string {
  if (!iso) return "—";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString("zh-CN", { hour12: false });
}

/** ACCOUNTS.md §3 — 我的设备：列出会话，可注销单个或其他全部. */
export function DevicesPage() {
  const qc = useQueryClient();
  const toast = useToast();
  const confirm = useConfirm();

  const q = useQuery({ queryKey: qk.sessions, queryFn: () => accountApi.sessions() });
  const sessions: DeviceSession[] = q.data?.sessions ?? [];

  const revoke = useMutation({
    mutationFn: (id: string) => accountApi.revokeSession(id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.sessions });
      toast.success("已经把那台设备注销了。");
    },
    onError: (err) => toast.error(friendlyMessage(err, "注销没有成功，请重试。")),
  });

  const revokeOthers = useMutation({
    mutationFn: () => accountApi.revokeOtherSessions(),
    onSuccess: (data) => {
      void qc.invalidateQueries({ queryKey: qk.sessions });
      toast.success(`已经注销其他 ${data.revoked} 台设备。`);
    },
    onError: (err) => toast.error(friendlyMessage(err, "注销没有成功，请重试。")),
  });

  if (q.isPending) {
    return (
      <div className="xz-container xz-stack" aria-busy="true">
        <Skeleton variant="rect" height={100} />
        <Skeleton variant="rect" height={100} />
      </div>
    );
  }

  const others = sessions.filter((s) => !s.current);

  return (
    <div className="xz-container xz-container--readable xz-stack">
      <h1 className="gz-type-heading">我的设备</h1>
      <p className="gz-muted">
        这里是所有还能用你账号的设备。看到不认识的，就把它注销掉，并且去改一次密码。
      </p>

      {sessions.length === 0 ? (
        <EmptyState
          icon={<Icon id="ui-info" size={24} />}
          title="没有可显示的登录记录"
          description="稍后再看看。"
        />
      ) : (
        sessions.map((s) => (
          <Card key={s.id} padding="md" className="xz-device">
            <div className="xz-device__head">
              <span className="xz-device__name">{deviceLabel(s.user_agent)}</span>
              {s.current && (
                <Tag tone="success">
                  <Icon id="ui-check-circle" size={16} /> 当前设备
                </Tag>
              )}
            </div>
            <p className="gz-caption">登录时间：{when(s.created_at)}</p>
            <p className="gz-caption">最近活动：{when(s.last_active_at)}</p>
            {!s.current && (
              <Button
                variant="danger"
                loading={revoke.isPending && revoke.variables === s.revoke_id}
                onClick={async () => {
                  const ok = await confirm({
                    title: "注销这台设备？",
                    message: "那台设备会立刻退出登录，需要重新输入密码。",
                    confirmLabel: "注销",
                    danger: true,
                  });
                  // the displayed `id` is truncated for other devices
                  if (ok) revoke.mutate(s.revoke_id);
                }}
              >
                <Icon id="ui-logout" size={20} /> 注销此设备
              </Button>
            )}
          </Card>
        ))
      )}

      {others.length > 0 && (
        <Button
          variant="danger"
          size="lg"
          block
          loading={revokeOthers.isPending}
          onClick={async () => {
            const ok = await confirm({
              title: "注销其他所有设备？",
              message: `除了你正在用的这台，另外 ${others.length} 台都会退出登录。`,
              confirmLabel: "全部注销",
              danger: true,
            });
            if (ok) revokeOthers.mutate();
          }}
        >
          注销其他所有设备
        </Button>
      )}
    </div>
  );
}
